/**
 * Edit Subcategory Dialog Component
 * Form to edit an existing subcategory with rename warning
 */

import { useState, useEffect } from 'react';
import {
  useUpdateSubcategory,
  useSubcategoryAffectedAssets,
  useCategoryStore,
} from '../../stores/categories';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { RenameWarningDialog } from './RenameWarningDialog';
import type { Subcategory } from '../../types/api';

interface EditSubcategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subcategory: Subcategory | null;
  onSuccess: () => void;
}

export function EditSubcategoryDialog({
  open,
  onOpenChange,
  subcategory,
  onSuccess,
}: EditSubcategoryDialogProps) {
  const [subcategoryName, setSubcategoryName] = useState('');
  const [parentCategoryId, setParentCategoryId] = useState<number>(0);
  const [error, setError] = useState('');
  const [showRenameWarning, setShowRenameWarning] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<any>(null);
  const [checkingAssets, setCheckingAssets] = useState(false);

  const updateMutation = useUpdateSubcategory();
  useCategoryStore.live();
  const categories = useCategoryStore(s => s.categories);

  const [affectedData, setAffectedData] = useState<any>(null);
  const checkAffectedAssets = useSubcategoryAffectedAssets();

  useEffect(() => {
    if (subcategory) {
      setSubcategoryName(subcategory.subcategory_name);
      setParentCategoryId(subcategory.parent_category_id);
    }
  }, [subcategory]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!subcategoryName.trim()) {
      setError('Subcategory name is required');
      return;
    }

    if (!subcategory) return;

    const updateData = {
      subcategory_name: subcategoryName.trim(),
      parent_category_id: parentCategoryId,
    };

    const nameChanged = subcategoryName.trim() !== subcategory.subcategory_name;

    if (nameChanged) {
      setCheckingAssets(true);
      setPendingUpdate(updateData);
      try {
        const result = await checkAffectedAssets.mutate({ subcategoryId: subcategory.subcategory_id, limit: 10 });
        setAffectedData(result);
        setCheckingAssets(false);
        if ((result as any).assetCount > 0) {
          setShowRenameWarning(true);
        } else {
          await performUpdate(updateData);
        }
      } catch (err) {
        setCheckingAssets(false);
        setError(err instanceof Error ? err.message : 'Failed to check affected assets');
      }
    } else {
      await performUpdate(updateData);
    }
  }


  async function performUpdate(data: any) {
    if (!subcategory) return;

    try {
      await updateMutation.mutate({
        subcategoryId: subcategory.subcategory_id,
        data,
      });
      handleClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update subcategory');
    }
  }

  async function handleRenameConfirm() {
    setShowRenameWarning(false);
    await performUpdate(pendingUpdate);
  }

  function handleRenameCancel() {
    setShowRenameWarning(false);
    setPendingUpdate(null);
    setAffectedData(null);
  }

  function handleClose() {
    if (!updateMutation.isPending && !checkingAssets) {
      setSubcategoryName('');
      setParentCategoryId(0);
      setError('');
      setShowRenameWarning(false);
      setPendingUpdate(null);
      setAffectedData(null);
      onOpenChange(false);
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Subcategory</DialogTitle>
            <DialogDescription>
              Update the subcategory details. Renaming will update all associated assets.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="subcategory-name">Subcategory Name *</Label>
                <Input
                  id="subcategory-name"
                  type="text"
                  placeholder="e.g., Laptops, Desks, Paintings"
                  value={subcategoryName}
                  onChange={(e) => setSubcategoryName(e.target.value)}
                  required
                  disabled={updateMutation.isPending || checkingAssets}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="parent-category">Parent Category</Label>
                <Select
                  value={parentCategoryId.toString()}
                  onValueChange={(value) => setParentCategoryId(parseInt(value, 10))}
                >
                  <SelectTrigger disabled={updateMutation.isPending || checkingAssets}>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.map((category) => (
                      <SelectItem key={category.category_id} value={category.category_id.toString()}>
                        {category.category_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending || checkingAssets}>
                {checkingAssets ? 'Checking...' : updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {subcategory && affectedData && (
        <RenameWarningDialog
          open={showRenameWarning}
          onOpenChange={setShowRenameWarning}
          entityType="subcategory"
          oldName={subcategory.subcategory_name}
          newName={subcategoryName.trim()}
          affectedAssets={affectedData.sampleAssets}
          assetCount={affectedData.assetCount}
          onConfirm={handleRenameConfirm}
          onCancel={handleRenameCancel}
          isLoading={updateMutation.isPending}
        />
      )}
    </>
  );
}
